import { useEffect, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { supabase } from '../services/supabase';
import { listOpenDrafts, saveEvaluationDraft } from '../services/evaluationDrafts';
import { fmtDate } from '../lib/evaluationFormat';
import { CATALOG_VERSION } from '../lib/evaluationCatalog';
import { OpcionesDeEntrenador } from './OpcionesDeEntrenador';
import {
  ROLES_ASIGNABLES,
  type EntrenadorAsignable,
} from '../lib/entrenadoresAsignables';
import { hoy } from '../lib/fechaLocal';

// Entrega 2 — paso previo a crear un informe desde dirección.
//
// Dirección no tiene la información que la cola del entrenador da gratis: qué
// entrenador firma y qué periodo se evalúa. Este diálogo pide las dos cosas
// antes de abrir el formulario, con el periodo prerrellenado con el bloque sin
// evaluar que ya calcula la mig 194.
//
// El entrenador que firma es OBLIGATORIO y se elige entre TODOS los monitores
// activos, no solo los que han dado sesiones a ese alumno: quien firma el
// informe es una decisión del club, no algo que se deduzca del histórico.
//
// El aviso de borradores anteriores existe porque el orden cronológico es
// club-wide: si queda un informe anterior sin publicar, éste no se podrá
// publicar hasta resolverlo. Vale más decirlo aquí que dejar que lo descubra
// después de puntuar 60 subcategorías.
//
// ── ASIGNAR (runbook §126) ────────────────────────────────────────────────
// Hasta ahora este diálogo no escribía NADA: elegir entrenador y periodo solo
// abría el formulario, y la única forma de dejar el informe en manos del
// entrenador era que dirección entrara en las 44 subcategorías y pulsara
// «Guardar borrador» al final. Nada se llamaba asignar, así que nadie asignaba:
// las dos evaluaciones que existen en producción las creó y publicó dirección
// en el mismo segundo.
//
// «Asignar» escribe el borrador EN BLANCO y cierra. Eso es lo que la 288 y la
// 289 llevan esperando desde que se aplicaron: la fila con `created_by` distinto
// de `monitor_id` entra en la cola del entrenador (source='asignado') y su
// trigger le manda el aviso. La base ya estaba hecha; faltaba el acto.
//
// EN BLANCO ES EN BLANCO: se manda `detailed_scores` sin una sola nota. Pasarlo
// por `withComputedAverages` —lo que hace el formulario al guardar— habría
// escrito las 44 subcategorías a DEFAULT_SCORE y el entrenador se habría
// encontrado un informe que nadie puntuó listo para publicar.


interface OpenDraft {
  id: string;
  period_start: string;
  period_end: string | null;
  monitor_name: string;
}

export interface CreateEvaluationModalProps {
  studentId: string;
  studentName: string;
  suggestedStart: string | null;
  suggestedEnd: string | null;
  onCancel: () => void;
  /** Dirección lo rellena ahora: abre el formulario, sin escribir nada todavía. */
  onConfirm: (params: {
    monitorId: string;
    monitorName: string;
    periodStart: string;
    periodEnd: string;
  }) => void;
  /** Asignado: el borrador ya está escrito y el diálogo se cierra. */
  onAssigned: (params: { monitorName: string; periodStart: string; periodEnd: string }) => void;
}

const today = () => hoy();

export default function CreateEvaluationModal({
  studentId,
  studentName,
  suggestedStart,
  suggestedEnd,
  onCancel,
  onConfirm,
  onAssigned,
}: CreateEvaluationModalProps) {
  const [monitors, setMonitors] = useState<EntrenadorAsignable[]>([]);
  const [monitorId, setMonitorId] = useState('');
  const [periodStart, setPeriodStart] = useState(suggestedStart ?? today());
  const [periodEnd, setPeriodEnd] = useState(suggestedEnd ?? today());
  const [drafts, setDrafts] = useState<OpenDraft[]>([]);
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState('');
  const [asignando, setAsignando] = useState(false);

  // Para el rótulo del botón: «Asignar a Jose Milla» dice a quién se le queda,
  // que es justo lo que hay que confirmar antes de pulsar.
  const monitorNombre = monitors.find((m) => m.id === monitorId)?.full_name ?? '';

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [{ data: mons, error }, open] = await Promise.all([
        // ⚠️ DIRECCIÓN TAMBIÉN FIRMA INFORMES. El #634 ensanchó Grupos y
        // Sesiones y dejó esta consulta fuera por clasificarla como gestión de
        // personal, y no lo es: aquí se elige QUIÉN FIRMA, que es una
        // asignación, igual que elegir quién lleva un grupo. El caso es real —
        // un director lleva Alevin A— y sin esto no había a quién elegir.
        //
        // `role` entra en el `select` porque el desplegable lo necesita para
        // bajar a Dirección a su propio grupo (`OpcionesDeEntrenador`).
        supabase
          .from('profiles')
          .select('id, full_name, role')
          .in('role', ROLES_ASIGNABLES)
          .eq('active', true)
          .order('full_name'),
        listOpenDrafts(studentId),
      ]);
      if (error) setErrMsg(error.message);
      setMonitors((mons ?? []) as EntrenadorAsignable[]);
      // El aviso de borradores abiertos existe para que nadie empiece un
      // informe que otro va a bloquear al publicar. Una lista vacía por un
      // fallo de lectura equivale a no tener el aviso, así que se dice.
      if (open.error)
        setErrMsg(`No se han podido comprobar los borradores abiertos: ${open.error}`);
      setDrafts(open.borradores);
      setLoading(false);
    })();
  }, [studentId]);

  // Las tres condiciones son las mismas para los dos botones: asignar escribe
  // la fila de verdad, así que no puede ser más laxo que abrir el formulario.
  function elegido(): { id: string; full_name: string } | null {
    if (!monitorId) {
      setErrMsg('Elige el entrenador que firma el informe.');
      return null;
    }
    if (!periodStart || !periodEnd) {
      setErrMsg('El periodo necesita fecha de inicio y de fin.');
      return null;
    }
    if (periodEnd < periodStart) {
      setErrMsg('El periodo termina antes de empezar.');
      return null;
    }
    const monitor = monitors.find((m) => m.id === monitorId);
    return { id: monitorId, full_name: monitor?.full_name ?? '' };
  }

  function submit() {
    const monitor = elegido();
    if (!monitor) return;
    onConfirm({
      monitorId: monitor.id,
      monitorName: monitor.full_name,
      periodStart,
      periodEnd,
    });
  }

  async function asignar() {
    const monitor = elegido();
    if (!monitor) return;
    setAsignando(true);
    setErrMsg('');
    const res = await saveEvaluationDraft({
      studentId,
      monitorId: monitor.id,
      periodStart,
      periodEnd,
      // Sin una sola nota. Lo que nadie ha puntuado no se escribe: ver la
      // cabecera de este fichero.
      detailedScores: { catalog_version: CATALOG_VERSION },
      overallComment: null,
      positionPrimary: null,
      positionSecondary: null,
      catalogVersion: CATALOG_VERSION,
    });
    setAsignando(false);
    // Un fallo aquí no puede cerrar el diálogo: la RPC rechaza por solape de
    // periodo o por un informe ya publicado, y esos dos se arreglan cambiando
    // las fechas SIN salir de aquí.
    if (!res.ok) {
      setErrMsg(res.message ?? 'No se pudo asignar el informe.');
      return;
    }
    onAssigned({ monitorName: monitor.full_name, periodStart, periodEnd });
  }

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      data-testid="create-eval-modal"
    >
      <div className="bg-bg-surface border border-bg-border rounded-xl w-full max-w-md p-5 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-oswald text-xl text-white">Crear informe</h3>
            <p className="font-oswald text-gold-primary">{studentName}</p>
          </div>
          <button onClick={onCancel} aria-label="cerrar" className="text-text-secondary">
            <X size={20} />
          </button>
        </div>

        {loading ? (
          <p className="text-gold-primary font-oswald animate-pulse">Cargando...</p>
        ) : (
          <>
            {drafts.length > 0 && (
              <div
                className="border border-warning rounded-lg p-3 flex gap-2"
                data-testid="create-eval-drafts-warning"
              >
                <AlertTriangle size={16} className="text-warning shrink-0 mt-0.5" />
                <div className="font-inter text-xs text-text-secondary space-y-1">
                  <p className="text-warning">
                    Este alumno ya tiene {drafts.length === 1 ? 'un informe' : 'informes'} sin
                    publicar:
                  </p>
                  {drafts.map((d) => (
                    <p key={d.id}>
                      {fmtDate(d.period_start)} → {fmtDate(d.period_end)} · {d.monitor_name}
                    </p>
                  ))}
                  <p>
                    Los informes de un alumno se publican en orden, así que habrá que publicar o
                    borrar los anteriores antes que éste.
                  </p>
                </div>
              </div>
            )}

            <label className="font-inter text-xs text-text-secondary block">
              Entrenador que firma el informe
              <select
                value={monitorId}
                onChange={(e) => setMonitorId(e.target.value)}
                data-testid="create-eval-monitor"
                className="mt-1 w-full bg-bg-elevated border border-bg-border rounded p-2 text-sm text-white"
              >
                <option value="">— Elige entrenador —</option>
                <OpcionesDeEntrenador lista={monitors} />
              </select>
            </label>

            <div className="grid grid-cols-2 gap-3">
              <label className="font-inter text-xs text-text-secondary block">
                Inicio del periodo
                <input
                  type="date"
                  value={periodStart}
                  onChange={(e) => setPeriodStart(e.target.value)}
                  data-testid="create-eval-start"
                  className="mt-1 w-full bg-bg-elevated border border-bg-border rounded p-2 text-sm text-white"
                />
              </label>
              <label className="font-inter text-xs text-text-secondary block">
                Fin del periodo
                <input
                  type="date"
                  value={periodEnd}
                  onChange={(e) => setPeriodEnd(e.target.value)}
                  data-testid="create-eval-end"
                  className="mt-1 w-full bg-bg-elevated border border-bg-border rounded p-2 text-sm text-white"
                />
              </label>
            </div>
            <p className="font-inter text-xs text-text-secondary">
              El periodo viene prerrellenado con el bloque de sesiones sin evaluar. La fecha de fin
              es la que reinicia el contador, así que conviene dejarla como el último día evaluado.
            </p>

            {errMsg && (
              <p className="text-[#FF4444] font-inter text-sm" data-testid="create-eval-error">
                {errMsg}
              </p>
            )}

            {/* El botón dice lo que hace. «Asignar» es el acto normal —dirección
                decide quién y cuándo, el entrenador lo escribe— y por eso es el
                principal; rellenarlo dirección sigue estando, debajo. */}
            <div className="space-y-2">
              <button
                onClick={asignar}
                disabled={asignando}
                data-testid="create-eval-assign"
                className="w-full bg-gold-primary text-black font-oswald rounded-lg py-2 disabled:opacity-50"
              >
                {asignando ? 'Asignando...' : `Asignar a ${monitorNombre || 'el entrenador'}`}
              </button>
              <p className="font-inter text-xs text-text-secondary text-center">
                Se crea el informe en blanco, le aparece en su lista de pendientes y le llega el
                aviso. No hace falta que lo rellenes tú.
              </p>
              <div className="flex gap-2 pt-1">
                <button
                  onClick={onCancel}
                  disabled={asignando}
                  className="flex-1 bg-bg-elevated border border-bg-border text-white font-oswald rounded-lg py-2 disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={submit}
                  disabled={asignando}
                  data-testid="create-eval-confirm"
                  className="flex-1 bg-bg-elevated border border-bg-border text-white font-oswald rounded-lg py-2 disabled:opacity-50"
                >
                  Rellenarlo yo ahora
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
